import React from 'react';
import classNames from 'classnames';
import intl from 'react-intl-universal';
import { TreeNode, TreeSelect } from '../TreeSelect/TreeSelect';
import { Button } from '../Button';
import type { ContextMenuInterface } from '../../elements/context-menu/ContextMenuInterface';
import './TreeSelectDropdownMenu.css';
import {Divider} from "../Divider/Divider";

export interface TreeSelectDropdownMenuProps extends ContextMenuInterface {
    treeData: TreeNode[];
    selectedIds: string[];
    onChange: (selectedIds: string[]) => void;
    maxHeight?: number;
    maxWidth?: number;
    selectAllLabel?: string;
    filterQuery?: string;
    onClearAll?: () => void;
    onSubmit?: () => void;
    submitLabel?: string;
    clearLabel?: string;
}

export const TreeSelectDropdownMenu: React.FC<TreeSelectDropdownMenuProps> = ({
    handleClose,
    isDark,
    treeData,
    selectedIds,
    onChange,
    maxHeight,
    maxWidth,
    selectAllLabel,
    filterQuery = '',
    onClearAll,
    onSubmit,
    submitLabel,
    clearLabel,
}) => {
    const handleSubmit = () => {
        onSubmit?.();
        handleClose();
    };

    const handleClear = () => {
        onChange([]);
        onClearAll?.();
    };

    const filterTree = (nodes: TreeNode[], query: string): TreeNode[] => {
        if (!query) return nodes;
        const lowerQuery = query.toLowerCase();

        return nodes
            .map(node => {
                const matchesSelf = node.label.toLowerCase().includes(lowerQuery);
                const filteredChildren = node.children ? filterTree(node.children, query) : undefined;
                const hasChildren = filteredChildren && filteredChildren.length > 0;

                if (matchesSelf || hasChildren) {
                    return {
                        ...node,
                        children: filteredChildren,
                    };
                }
                return null;
            })
            .filter(Boolean) as TreeNode[];
    };

    const filteredTreeData = filterTree(treeData, filterQuery);

    return (
        <div
            className={classNames({
                'TreeSelectDropdownMenu': true,
                'theme-dark': isDark,
            })}
            style={{
                maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            }}
        >
            <div className="TreeSelectDropdownMenu__body">
                <TreeSelect
                    treeData={filteredTreeData}
                    selectedIds={selectedIds}
                    onChange={onChange}
                    maxHeight={maxHeight}
                    withSelectAll={true}
                    selectAllLabel={selectAllLabel}
                    className="TreeSelectDropdownMenu__tree"
                />
            </div>

            {(onClearAll || onSubmit) && (
                <>
                    <Divider/>
                    <div className="TreeSelectDropdownMenu__footer">
                        {onClearAll && (
                            <Button
                                text={clearLabel || intl.get('common.clear_all').d('Clear all')}
                                style="plain"
                                onClick={handleClear}
                            />
                        )}
                        {onSubmit && (
                            <Button
                                text={submitLabel || intl.get('common.submit').d('Submit')}
                                style="solid"
                                type="primary"
                                onClick={handleSubmit}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
